import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { emissionValueWithDecimals } from '../../../../../../../../../../../shared/helpers/emission-value-with-decimals';
import { DeclaredUnit } from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import {
  EmissionSymbol,
  IProcessDetails,
} from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { MachineType } from '../../../PcfWizard.type';
import AuxiliaryEquipmentValues from './AuxiliaryEquipmentValues';

type ExpandableInjectionMoldingMachineDetailsProps = {
  machines: IProcessDetails['machines'];
  totalMachineEmissions?: number;
};

const ExpandableInjectionMoldingMachineDetails = ({
  machines,
  totalMachineEmissions,
}: ExpandableInjectionMoldingMachineDetailsProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const mainMachine = machines?.find((machine) => {
    return machine.type === MachineType.MAIN;
  });

  const auxiliaryMachines =
    machines?.filter((machine) => {
      return machine.type === MachineType.AUXILIARY;
    }) || [];

  const onExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  const mainMachineContent = (
    <div className='flex flex-column gap-4'>
      <div className='grid w-full grid-nogutter'>
        {/* Manufacturer */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.manufacturer')}
          </span>
          <span className='text-lg'>{mainMachine?.manufacturer?.name}</span>
        </div>

        {/* Model/Series */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.modelSeries')}
          </span>
          <span className='text-lg'>{mainMachine?.model}</span>
        </div>
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Number of Cavities */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.numberOfCavities',
            )}
          </span>
          <span className='text-lg'>{mainMachine?.number_of_cavities}</span>
        </div>

        {/* Cycle Time */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.cycleTime')}
          </span>
          <span className='text-lg'>{mainMachine?.cycle_time} s</span>
        </div>
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Operating Power */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.ratedPower')}
          </span>
          <span className='text-lg'>{mainMachine?.rated_power} kW</span>
        </div>

        {/* Energy per Piece */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.energyPerPiece',
            )}
          </span>
          <span className='text-lg'>
            {emissionValueWithDecimals(mainMachine?.energy_per_piece)} kWh
          </span>
        </div>
      </div>
    </div>
  );

  const auxiliaryEquipmentsContent = auxiliaryMachines.length && (
    <div className='flex flex-column gap-4 w-full'>
      <span className='text-lg font-medium'>
        {t('calculationDetailsPage.sections.injectionMoldingProcess.auxiliaryEquipments.title')}
      </span>

      {auxiliaryMachines.map((machine, index) => {
        return <AuxiliaryEquipmentValues machine={machine} key={index} />;
      })}
    </div>
  );

  const expandedContent = (
    <div className='flex flex-column gap-4'>
      {/* Main Machine Details */}
      {mainMachineContent}

      {/* Auxiliary Equipment Details */}
      {auxiliaryEquipmentsContent}

      <Divider className='p-2 m-0 opacity-60' />
    </div>
  );

  return (
    <div className='flex flex-column gap-3 w-full'>
      {/* Title & Expand Button */}
      <div className='flex flex-row gap-2 align-items-center'>
        <span className='text-lg font-medium'>
          {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.title')}
        </span>

        <Button
          icon={isExpanded ? 'pi pi-angle-up' : 'pi pi-angle-down'}
          onClick={onExpandClick}
          text
          className='p-2 m-0 -my-1 text-black-alpha-90 w-min hover:bg-black-alpha-20 border-round-lg bg-black-alpha-10 opacity-60'
          pt={{ icon: { className: 'font-bold' } }}
        />
      </div>

      {/* Expanded Content */}
      {isExpanded && expandedContent}

      {/* Emissions */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.sections.injectionMoldingProcess.mainMachine.emissions')}
        </span>

        <EmissionValueWithSuffix
          emissionValue={totalMachineEmissions}
          declaredUnit={DeclaredUnit.piece}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      </div>
    </div>
  );
};

export default ExpandableInjectionMoldingMachineDetails;
